import React, { useState, useMemo } from 'react';
import { useUser } from '../../../contexts/userContext';
import { useColumns } from '../../../contexts/columnsContext';
import Header from '../../Header';
import apiUploads from '../../../services/apiUploads';
import api from '../../../services/api';
import './style.css';

function ImportarPVATPage() {
  const { user } = useUser();
  const { columns } = useColumns();

  // Filtros
  const [dataInicial, setDataInicial] = useState('');
  const [dataFinal, setDataFinal] = useState('');

  // Dados
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');

  // Seleção
  const [selecionados, setSelecionados] = useState([]);

  // Coluna destino
  const [columnId, setColumnId] = useState('');

  // Resultado da importação
  const [resultado, setResultado] = useState(null);
  const [importando, setImportando] = useState(false);

  // -------------------------------------------------------
  // Buscar pedidos PVAT do Faktory
  // -------------------------------------------------------
  const handleBuscar = async () => {
    if (!dataInicial || !dataFinal) {
      setErro('Informe as duas datas para buscar.');
      return;
    }
    setErro('');
    setResultado(null);
    setSelecionados([]);
    setPedidos([]);
    setLoading(true);

    try {
      const resp = await apiUploads.get('/faktory/pedidovenda/list-pvat', {
        params: { dataInicial, dataFinal },
      });
      setPedidos(resp.data || []);
      if ((resp.data || []).length === 0) {
        setErro('Nenhum pedido PVAT encontrado no período informado.');
      }
    } catch (e) {
      setErro('Erro ao buscar pedidos. Verifique as datas e tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  // -------------------------------------------------------
  // Seleção de linhas
  // -------------------------------------------------------
  const toggleSelecionado = (numero) => {
    setSelecionados((prev) =>
      prev.includes(numero) ? prev.filter((n) => n !== numero) : [...prev, numero],
    );
  };

  const selecionarTodos = () => {
    if (selecionados.length === pedidos.length) {
      setSelecionados([]);
    } else {
      setSelecionados(pedidos.map((p) => p.numero));
    }
  };

  const todosSelecionados = pedidos.length > 0 && selecionados.length === pedidos.length;

  // -------------------------------------------------------
  // Criar cards
  // -------------------------------------------------------
  const handleCriarCards = async () => {
    if (selecionados.length === 0) {
      setErro('Selecione ao menos um pedido.');
      return;
    }
    if (!columnId) {
      setErro('Selecione a coluna de destino.');
      return;
    }
    if (!user) {
      setErro('Usuário não identificado. Faça login novamente.');
      return;
    }

    setErro('');
    setResultado(null);
    setImportando(true);

    try {
      const pedidosSelecionados = pedidos.filter((p) =>
        selecionados.includes(p.numero),
      );

      const cards = pedidosSelecionados.map((p) => ({
        name:          p.cliente || `Pedido PVAT ${p.numero}`,
        pedido_number: String(p.numero),
        produto:       'Assistência Técnica',
        sale_value:    p.valor || 0,
        column_id:     parseInt(columnId, 10),
        entity_id:     user.id,
        empresa_id:    user.empresa_id,
        state:         p.estado || '',
        city:          p.cidade || '',
      }));

      const resp = await api.post('/card/import-pvat', { cards });
      const { created, skipped } = resp.data;

      setResultado({
        tipo: 'sucesso',
        msg: `${created} card(s) criado(s) com sucesso.${skipped > 0 ? ` ${skipped} já existiam e foram ignorados.` : ''}`,
      });
      setSelecionados([]);
    } catch (e) {
      setResultado({
        tipo: 'erro',
        msg: 'Erro ao criar cards. Tente novamente.',
      });
    } finally {
      setImportando(false);
    }
  };

  // -------------------------------------------------------
  // Formatações
  // -------------------------------------------------------
  const formatarData = (valor) => {
    if (!valor) return '-';
    try {
      return new Date(valor).toLocaleDateString('pt-BR');
    } catch {
      return String(valor);
    }
  };

  const formatarValor = (valor) => {
    return Number(valor || 0).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });
  };

  // Colunas acessíveis ao usuário (filtradas pela empresa)
  const colunasFiltradas = useMemo(
    () => (columns || []).filter((c) => c.empresa_id === user?.empresa_id || !c.empresa_id),
    [columns, user],
  );

  return (
    <div className="importar-pvat-wrapper">
      <Header />

      <div className="importar-pvat-body">
        <h2 style={{ margin: '0 0 16px 0', fontSize: 18, fontWeight: 700, color: '#1e293b' }}>
          Importar Pedidos PVAT do Faktory
        </h2>

        {/* Filtros de data */}
        <div className="importar-pvat-filtros">
          <label>
            Data inicial
            <input
              type="date"
              value={dataInicial}
              onChange={(e) => setDataInicial(e.target.value)}
            />
          </label>
          <label>
            Data final
            <input
              type="date"
              value={dataFinal}
              onChange={(e) => setDataFinal(e.target.value)}
            />
          </label>
          <button
            className="btn-buscar-pvat"
            onClick={handleBuscar}
            disabled={loading}
          >
            {loading ? 'Buscando...' : 'Buscar Pedidos'}
          </button>
        </div>

        {/* Resultado da importação */}
        {resultado && (
          <div className={`importar-pvat-resultado ${resultado.tipo}`}>
            {resultado.msg}
          </div>
        )}

        {/* Mensagem de erro */}
        {erro && (
          <div className="importar-pvat-resultado erro">{erro}</div>
        )}

        {/* Barra de ações (só aparece quando há pedidos) */}
        {pedidos.length > 0 && (
          <div className="importar-pvat-acoes">
            <button
              className="btn-selecionar-todos-pvat"
              onClick={selecionarTodos}
            >
              {todosSelecionados ? 'Desmarcar todos' : 'Selecionar todos'}
            </button>

            <span>
              {selecionados.length} de {pedidos.length} selecionado(s)
            </span>

            <select
              className="importar-pvat-select-coluna"
              value={columnId}
              onChange={(e) => setColumnId(e.target.value)}
            >
              <option value="">-- Selecione a coluna --</option>
              {colunasFiltradas.map((col) => (
                <option key={col.id} value={col.id}>
                  {col.name}
                </option>
              ))}
            </select>

            <button
              className="btn-criar-cards-pvat"
              onClick={handleCriarCards}
              disabled={importando || selecionados.length === 0 || !columnId}
            >
              {importando ? 'Criando...' : `Criar ${selecionados.length} Card(s)`}
            </button>
          </div>
        )}

        {/* Tabela de pedidos */}
        {loading && (
          <div className="importar-pvat-loading">Buscando pedidos no Faktory...</div>
        )}

        {!loading && pedidos.length > 0 && (
          <div className="importar-pvat-tabela-container">
            <table className="importar-pvat-tabela">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={todosSelecionados}
                      onChange={selecionarTodos}
                      title="Selecionar todos"
                    />
                  </th>
                  <th>Nº Pedido</th>
                  <th>Data</th>
                  <th>Cliente</th>
                  <th>Cidade / UF</th>
                  <th>Vendedor</th>
                  <th>Valor</th>
                </tr>
              </thead>
              <tbody>
                {pedidos.map((pedido) => {
                  const marcado = selecionados.includes(pedido.numero);
                  return (
                    <tr
                      key={pedido.numero}
                      className={marcado ? 'pvat-row-selecionada' : ''}
                      onClick={() => toggleSelecionado(pedido.numero)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={marcado}
                          onChange={() => toggleSelecionado(pedido.numero)}
                        />
                      </td>
                      <td>
                        <span className="pvat-badge-numero">
                          {pedido.numero}
                        </span>
                      </td>
                      <td>{formatarData(pedido.data)}</td>
                      <td>{pedido.cliente || '-'}</td>
                      <td>
                        {[pedido.cidade, pedido.estado].filter(Boolean).join(' / ') || '-'}
                      </td>
                      <td>{pedido.vendedor || '-'}</td>
                      <td className="pvat-valor">
                        {formatarValor(pedido.valor)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!loading && pedidos.length === 0 && !erro && (
          <div className="importar-pvat-empty">
            Informe o período e clique em "Buscar Pedidos" para listar os pedidos PVAT do Faktory.
          </div>
        )}
      </div>
    </div>
  );
}

export default ImportarPVATPage;
